import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { loadProperties, loadPropDetails, savePartnerProperties } from '../actionCreators';
import OfferPartnerPropertyComponent from './OfferPartnerPropertyComponent';
import Description from '@mui/icons-material/Description';
import { Grid } from '@mui/material';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { withRights } from 'providers/RightsConsumer';
import { Prompt } from 'react-router-dom';
import { emptyString } from 'constants/emptyConst';
import { Button } from '@totalsoft/rocket-ui';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { GridActionsWrapper } from 'components/common/CommonStyles';
import { useToast } from 'utils/hooks';

const PropertyContainer = ({ partnerId, documentId, siteId, t, rights }) => {
  const [localProperties, setPropertyList] = useState([]);
  const [cardExpanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [savedWithMandatoryPropertiesEmpty, setSavedWithMandatoryPropertiesEmpty] = useState(false);
  const addToast = useToast();

  const dispatch = useDispatch();

  const saving = useSelector(state => state.currentOffer.clientPropertiesData[partnerId]?.saving, shallowEqual);
  const properties = useSelector(
    state => (state.entities.offerPartnerProperties ? state.entities.offerPartnerProperties[partnerId] : []),
    shallowEqual
  );

  const propertiesLoaded = useSelector(
    state => (state.currentOffer.clientPropertiesData[partnerId] ? state.currentOffer.clientPropertiesData[partnerId].loaded : false),
    shallowEqual
  );

  useEffect(() => {
    if (!cardExpanded) return;
    if (propertiesLoaded) {
      setPropertyList(properties);
      setLoading(false);
    } else {
      setLoading(true);
      dispatch(loadProperties(partnerId, documentId, siteId));
    }
  }, [cardExpanded, dispatch, documentId, partnerId, properties, propertiesLoaded, siteId]);

  const onExpand = (_, expanded) => {
    setExpanded(!expanded);
  };

  const loadPropertyDetails = useCallback(
    propId => {
      return dispatch(loadPropDetails(propId)).then(data => {
        return data.map(item => ({ value: item.value, label: item.value }));
      });
    },
    [dispatch]
  );

  const onPropertyChange = idx => prop => value => {
    setPropertyList(
      Object.assign([], {
        ...localProperties,
        [idx]: {
          ...localProperties[idx],
          [prop]: value
        }
      })
    );
    setSaveDisabled(false);
  };

  const saveProperties = (partnerId, localProperties) => {
    if (mandatoryPropertiesAreEmpty()) {
      setSavedWithMandatoryPropertiesEmpty(true);
      return;
    }
    dispatch(savePartnerProperties(documentId, siteId, partnerId, localProperties)).then(() => {
      addToast(t('OfferEdit.Client.Properties.PropertiesUpdated'), 'success');
    });
    setSaveDisabled(true);
  };

  const mandatoryPropertiesAreEmpty = useCallback(() => {
    return localProperties.some(
      x => x.mandatory && !(rights.readOnlyFull || x.readOnly) && (x.propertyValue === emptyString || x.propertyValue === null)
    );
  }, [localProperties, rights.readOnlyFull]);

  return (
    <CollapseOrBlankCard
      id='propertyCard'
      icon={Description}
      title={t('OfferEdit.Client.Properties.Title')}
      expanded={cardExpanded}
      onToggle={onExpand}
      canExpand={partnerId != null}
    >
      {loading && !localProperties ? (
        <WrappedFakeText lines={6} />
      ) : (
        <Grid container spacing={2}>
          <Prompt when={!saveDisabled} message={t('LeavingWithoutSaving')} />
          {localProperties.map((item, idx) => {
            return (
              <OfferPartnerPropertyComponent
                key={idx}
                idx={idx}
                item={item}
                loadPropertyDetails={loadPropertyDetails}
                onPropertyChange={onPropertyChange}
                setSaveDisabled={setSaveDisabled}
                savedWithMandatoryPropertiesEmpty={savedWithMandatoryPropertiesEmpty}
              />
            );
          })}
          <GridActionsWrapper mt={2} item xs={12}>
            <Button
              id='property_saveBtn'
              onClick={() => saveProperties(partnerId, localProperties)}
              disabled={
                rights.readOnlyFull || saveDisabled || (mandatoryPropertiesAreEmpty() && savedWithMandatoryPropertiesEmpty) || saving
              }
            >
              {saving ? t('Saving') : t('Save')}
            </Button>
          </GridActionsWrapper>
        </Grid>
      )}
    </CollapseOrBlankCard>
  );
};

PropertyContainer.propTypes = {
  actions: PropTypes.object,
  partnerId: PropTypes.number,
  properties: PropTypes.array,
  propertiesLoaded: PropTypes.bool,
  t: PropTypes.func.isRequired,
  setIsDirty: PropTypes.func,
  rights: PropTypes.object.isRequired,
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired
};

export default withTranslation()(withRights(PropertyContainer));
